import React, { useEffect, useRef, useCallback } from 'react';

export interface UseAutoScrollOptions {
  /**
   * Whether auto-scroll is enabled
   */
  enabled?: boolean;

  /**
   * Dependencies that trigger scroll (typically [conversationHistory.length, completion])
   */
  dependencies?: any[];

  /**
   * Whether currently streaming/loading content
   */
  isStreaming?: boolean;
}

export interface UseAutoScrollReturn {
  /**
   * Ref to attach to the scrollable container
   */
  containerRef: React.RefObject<HTMLDivElement>;

  /**
   * Ref to attach to an element at the end of scrollable content
   */
  scrollAnchorRef: React.RefObject<HTMLDivElement>;

  /**
   * Ref to attach to the last user message element for ChatGPT-style scrolling
   */
  lastUserMessageRef: React.RefObject<HTMLDivElement>;

  /**
   * Manually trigger scroll to bottom
   */
  scrollToBottom: () => void;

  /**
   * Scroll the last user message to the top of viewport (ChatGPT-style)
   */
  scrollUserMessageToTop: () => void;
}

/**
 * ChatGPT-style auto-scroll hook for chat interfaces
 *
 * Key behavior:
 * 1. When user submits a message, scroll that message to the TOP of the viewport
 *    (with empty space below for the response to stream into)
 * 2. During streaming, NO auto-scroll - response fills in the space below
 * 3. User can scroll freely at any time
 * 4. When loading existing conversation, scroll to bottom instantly
 *
 * IMPORTANT: Requires a dynamic spacer element that:
 * - Expands INSTANTLY to ~70vh when isLoading becomes true
 * - Shrinks smoothly when isLoading becomes false
 */
export const useAutoScroll = ({
  enabled = true,
  dependencies = [],
  isStreaming = false,
}: UseAutoScrollOptions = {}): UseAutoScrollReturn => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollAnchorRef = useRef<HTMLDivElement>(null);
  const lastUserMessageRef = useRef<HTMLDivElement>(null);

  // Track conversation length to detect new messages
  const lastConversationLengthRef = useRef(0);

  // Track if this is the initial mount (for loading existing conversations)
  const isInitialMountRef = useRef(true);

  // Track previous streaming state to detect transitions
  const wasStreamingRef = useRef(false);

  /**
   * Scroll to absolute bottom (for initial load)
   */
  const scrollToBottom = useCallback(() => {
    if (!enabled || !scrollAnchorRef.current) return;

    scrollAnchorRef.current.scrollIntoView({
      behavior: 'instant',
      block: 'end',
    });
  }, [enabled]);

  /**
   * ChatGPT-style: Scroll the last user message to the TOP of the viewport
   * This positions the user's question at the top with empty space below
   * for the streaming response to fill in.
   * Uses smooth animation so user can see their message scroll up.
   */
  const scrollUserMessageToTop = useCallback(() => {
    if (!enabled || !lastUserMessageRef.current) return;

    // Scroll the user message to the top with smooth animation
    lastUserMessageRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [enabled]);

  /**
   * Handle initial mount - scroll to bottom for existing conversations
   */
  useEffect(() => {
    if (!enabled) return;

    const conversationLength = dependencies[0] || 0;

    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      lastConversationLengthRef.current = conversationLength;

      // If loading an existing conversation, scroll to bottom
      if (conversationLength > 0) {
        requestAnimationFrame(() => {
          scrollToBottom();
        });
      }
    }
  }, [enabled, dependencies, scrollToBottom]);

  /**
   * Handle streaming start - scroll user message to top
   * This triggers when isStreaming transitions from false to true
   */
  useEffect(() => {
    if (!enabled) return;

    // Detect streaming START (false -> true transition)
    if (isStreaming && !wasStreamingRef.current) {
      // Streaming just started - scroll user message to top
      // Use double RAF to ensure:
      // 1. The spacer has expanded (it's instant, but DOM needs to update)
      // 2. The lastUserMessageRef is attached to the new message
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollUserMessageToTop();
        });
      });
    }

    wasStreamingRef.current = isStreaming;
  }, [enabled, isStreaming, scrollUserMessageToTop]);

  /**
   * Track conversation length changes
   */
  useEffect(() => {
    if (!enabled || isInitialMountRef.current) return;

    const conversationLength = dependencies[0] || 0;
    lastConversationLengthRef.current = conversationLength;
  }, [enabled, dependencies]);

  return {
    containerRef,
    scrollAnchorRef,
    lastUserMessageRef,
    scrollToBottom,
    scrollUserMessageToTop,
  };
};
